import React, { useEffect, useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XCircleIcon, CheckCircleIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import * as pdfjs from 'pdfjs-dist';
import { renderAsync } from 'docx-preview';

const DOCX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

const DocumentPreviewModal = ({
  showDocumentPreviewModal,
  setShowDocumentPreviewModal,
  documentPreviewTitle,
  setDocumentPreviewTitle,
  documentPreviewLoading,
  setDocumentPreviewLoading,
  documentPreviewUrl,
  setDocumentPreviewUrl,
  // A Word file (Blob) rendered in the app; takes the place of documentPreviewUrl.
  documentPreviewDocx = null,
  // A downloaded PDF, image or text file (Blob), shown from memory. Servers whose
  // Content-Security-Policy has no blob: in connect-src/frame-src block the
  // PDF viewer and frames from loading blob: links, so no link is made for it.
  documentPreviewFile = null,
  previewScrollContainerRef,
  handlePreviewScroll,
  isPreviewScrollCompleted,
  setIsPreviewScrollCompleted,
  handleAgreeDocument,
  previewDocumentMeta,
  setPreviewDocumentMeta,
  previewPageCount,
  setPreviewPageCount,
  previewCurrentPage,
  setPreviewCurrentPage,
  user,
  showIAgree = true,
  // controls acknowledgement props
  docControls = [],
  showToEmployees = false,
  userRole = '',
  onConfirmControls,
  // approval props
  showApproveReject = false,
  onApprove,
  onReject,
  // file navigation props
  pendingFiles = [],
  currentFileIndex = 0,
  setCurrentFileIndex,
}) => {
  const [numPages, setNumPages] = useState(0);
  const [viewerWidth, setViewerWidth] = useState(900);
  const [pdfState, setPdfState] = useState(null);

  // controls step state
  const [showControlsStep, setShowControlsStep] = useState(false);
  const [checkedControlIds, setCheckedControlIds] = useState([]);
  const [ackLoading, setAckLoading] = useState(false);
  // Documents are normally shown in the app (PDF viewer, Word renderer, or a
  // same-origin frame), where reaching the end enables I Agree by itself. Only
  // a cross-origin frame (the Office viewer for old .doc files, or a link
  // fallback) cannot be inspected; there the reader confirms manually.
  const [iframeReviewUnverifiable, setIframeReviewUnverifiable] = useState(false);
  const [docxState, setDocxState] = useState('idle'); // idle | loading | ready | error

  const scrollContainerRef = useRef(null);
  const docxContainerRef = useRef(null);

  // What kind of downloaded file this is (documentPreviewFile), and the Word file to render.
  const fileType = (documentPreviewFile && documentPreviewFile.type) || '';
  const docxFile = documentPreviewDocx || (fileType === DOCX_MIME_TYPE ? documentPreviewFile : null);
  const isPdfFile = fileType === 'application/pdf';
  const isImageFile = fileType.startsWith('image/');
  const isTextFile = fileType.startsWith('text/');
  // Anything else (old .doc, spreadsheets, ...) cannot be shown in the app: offer a download.
  const otherFile = documentPreviewFile && !docxFile && !isPdfFile && !isImageFile && !isTextFile
    ? documentPreviewFile : null;

  // Controls step: the applicable controls still to acknowledge, and "Select all".
  const pendingControlIds = docControls
    .filter((c) => c.is_applicable && !c.is_acknowledged)
    .map((c) => c.control_id);
  const allControlsSelected =
    pendingControlIds.length > 0 && pendingControlIds.every((id) => checkedControlIds.includes(id));
  const selectAllRef = useRef(null);
  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = checkedControlIds.length > 0 && !allControlsSelected;
    }
  });

  const needsControlsStep =
    showIAgree &&
    !showToEmployees &&
    docControls.length > 0 &&
    (userRole === 'customer_admin' || userRole === 'security_analyst');

  const onClose = () => {
    setShowDocumentPreviewModal(false);
    setDocumentPreviewUrl('');
    setDocumentPreviewTitle('Document Preview');
    setDocumentPreviewLoading(false);
    setPreviewPageCount(1);
    setPreviewCurrentPage(1);
    setIsPreviewScrollCompleted(false);
    setPreviewDocumentMeta(null);
    setNumPages(0);
    setPdfState(null);
    setShowControlsStep(false);
    setCheckedControlIds([]);
    setIframeReviewUnverifiable(false);
  };

  useEffect(() => {
    setIsPreviewScrollCompleted(false);
    setNumPages(0);
    setPdfState(documentPreviewUrl || documentPreviewFile ? null : false);
    setShowControlsStep(false);
    setCheckedControlIds([]);
    setIframeReviewUnverifiable(false);
  }, [documentPreviewUrl, documentPreviewDocx, documentPreviewFile]);

  // In-memory images and text: an image link (img-src allows blob:) or the text itself.
  const [fileImageUrl, setFileImageUrl] = useState('');
  const [fileText, setFileText] = useState(null);
  useEffect(() => {
    setFileImageUrl('');
    setFileText(null);
    if (!documentPreviewFile) return undefined;
    if (isImageFile) {
      const url = URL.createObjectURL(documentPreviewFile);
      setFileImageUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    if (isTextFile) {
      let cancelled = false;
      documentPreviewFile.text().then((text) => { if (!cancelled) setFileText(text); });
      return () => { cancelled = true; };
    }
    return undefined;
  }, [documentPreviewFile, isImageFile, isTextFile]);

  // Short content that fits without scrolling counts as read once shown.
  const markReadIfNoScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (el && el.scrollHeight <= el.clientHeight + 80) setIsPreviewScrollCompleted(true);
  }, [setIsPreviewScrollCompleted]);
  useEffect(() => {
    if (fileText !== null) requestAnimationFrame(markReadIfNoScroll);
  }, [fileText, markReadIfNoScroll]);

  // Render a Word file into the scrollable body, then check whether it fits
  // without scrolling (a one-page document counts as read once shown).
  useEffect(() => {
    if (!docxFile || !showDocumentPreviewModal) return undefined;
    let cancelled = false;
    setDocxState('loading');
    const render = async () => {
      // Wait for the container to mount.
      await new Promise((resolve) => requestAnimationFrame(resolve));
      const target = docxContainerRef.current;
      if (cancelled || !target) return;
      try {
        target.innerHTML = '';
        await renderAsync(docxFile, target, null, {
          className: 'docx-preview-body',
          inWrapper: true,
          breakPages: true,
        });
        if (cancelled) return;
        setDocxState('ready');
        const el = scrollContainerRef.current;
        if (el && el.scrollHeight <= el.clientHeight + 80) setIsPreviewScrollCompleted(true);
      } catch (err) {
        console.error('Word document could not be rendered:', err);
        if (!cancelled) setDocxState('error');
      }
    };
    render();
    return () => { cancelled = true; };
  }, [docxFile, showDocumentPreviewModal, setIsPreviewScrollCompleted]);

  useEffect(() => {
    const updateWidth = () => {
      if (!scrollContainerRef.current) return;
      setViewerWidth(Math.max(280, scrollContainerRef.current.clientWidth - 32));
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [showDocumentPreviewModal, pdfState]);

  const handleScroll = useCallback((event) => {
    const el = event.currentTarget;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setIsPreviewScrollCompleted(distanceFromBottom <= 80);
  }, [setIsPreviewScrollCompleted]);

  const onLastPageRendered = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const needsScroll = el.scrollHeight > el.clientHeight + 80;
    if (!needsScroll) setIsPreviewScrollCompleted(true);
  }, [setIsPreviewScrollCompleted]);

  const onDocumentLoadSuccess = ({ numPages: loaded }) => {
    setNumPages(loaded);
    setPreviewPageCount(loaded);
    setDocumentPreviewLoading(false);
    setPdfState(true);
  };

  const onDocumentLoadError = () => {
    setDocumentPreviewLoading(false);
    setPdfState(false);
  };

  const handleIAgreeClick = () => {
    if (needsControlsStep) {
      // Pre-check all unacknowledged applicable controls
      setCheckedControlIds(
        docControls
          .filter((c) => c.is_applicable && !c.is_acknowledged)
          .map((c) => c.control_id)
      );
      setShowControlsStep(true);
    } else {
      handleAgreeDocument();
    }
  };

  const toggleControl = (controlId) => {
    setCheckedControlIds((prev) =>
      prev.includes(controlId) ? prev.filter((id) => id !== controlId) : [...prev, controlId]
    );
  };

  const toggleAllControls = () => {
    setCheckedControlIds(allControlsSelected ? [] : pendingControlIds);
  };

  const handleConfirm = async () => {
    if (checkedControlIds.length === 0) return;
    setAckLoading(true);
    try {
      await onConfirmControls(checkedControlIds);
    } finally {
      setAckLoading(false);
      setShowControlsStep(false);
    }
  };

  const renderBody = () => {
    if (showControlsStep) {
      const unacknowledged = docControls.filter((c) => c.is_applicable && !c.is_acknowledged);
      const alreadyDone = docControls.filter((c) => c.is_acknowledged);
      const codeTag = 'inline-flex min-w-[4.5rem] shrink-0 justify-center rounded-md px-2 py-0.5 font-mono text-xs font-medium';
      return (
        <div>
          <div className="px-6 py-5">
            <h4 className="text-base font-semibold text-gray-900">Which controls does this document cover?</h4>
            <p className="mt-1 text-sm text-gray-500">
              Acknowledging records that you have reviewed this document for each selected control.
            </p>

            {unacknowledged.length > 0 && (
              <div className="mt-5 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                <label className="flex cursor-pointer items-center gap-3 border-b border-gray-200 bg-gray-50 px-4 py-2.5">
                  <input
                    ref={selectAllRef}
                    type="checkbox"
                    checked={allControlsSelected}
                    onChange={toggleAllControls}
                    aria-label="Select all controls"
                    className="h-4 w-4 shrink-0 rounded border-gray-300 accent-primary-700"
                  />
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">Select all</span>
                  <span className="ml-auto text-xs text-gray-500">
                    {unacknowledged.length} control{unacknowledged.length !== 1 ? 's' : ''} to acknowledge
                  </span>
                </label>
                <ul className="divide-y divide-gray-100">
                  {unacknowledged.map((ctrl) => {
                    const isChecked = checkedControlIds.includes(ctrl.control_id);
                    return (
                      <li key={ctrl.control_id}>
                        <label
                          className={`flex cursor-pointer items-center gap-3 px-4 py-3 transition-colors ${
                            isChecked ? 'bg-primary-50/50' : 'hover:bg-gray-50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleControl(ctrl.control_id)}
                            className="h-4 w-4 shrink-0 rounded border-gray-300 accent-primary-700"
                          />
                          <span className={`${codeTag} bg-gray-100 text-gray-700`}>{ctrl.control_code}</span>
                          <span className="text-sm text-gray-800">{ctrl.control_name}</span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {alreadyDone.length > 0 && (
              <div className="mt-6">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Already acknowledged ({alreadyDone.length})
                </p>
                <ul className="divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-200 bg-white">
                  {alreadyDone.map((ctrl) => (
                    <li key={ctrl.control_id} className="flex items-center gap-3 px-4 py-3">
                      <CheckCircleIcon className="h-4 w-4 shrink-0 text-green-600" />
                      <span className={`${codeTag} bg-gray-50 text-gray-500`}>{ctrl.control_code}</span>
                      <span className="text-sm text-gray-500">{ctrl.control_name}</span>
                      <span className="ml-auto shrink-0 text-xs font-medium text-green-700">Acknowledged</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      );
    }

    if (documentPreviewLoading) {
      return (
        <div className="h-full flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      );
    }

    if (docxFile) {
      return (
        <div
          ref={scrollContainerRef}
          onScroll={handleScroll}
          className="w-full h-full overflow-y-auto"
        >
          {docxState === 'loading' && (
            <div className="flex items-center justify-center h-40">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          )}
          {docxState === 'error' && (
            <div className="h-40 flex items-center justify-center text-gray-500 text-sm">
              This Word document could not be displayed.
            </div>
          )}
          <div ref={docxContainerRef} className="px-4 pb-4" />
        </div>
      );
    }

    if (otherFile) {
      const download = () => {
        const url = URL.createObjectURL(otherFile);
        const link = document.createElement('a');
        link.href = url;
        link.download = documentPreviewTitle || 'document';
        link.click();
        setTimeout(() => URL.revokeObjectURL(url), 10000);
      };
      return (
        <div className="h-full flex flex-col items-center justify-center gap-3 text-sm text-gray-500">
          This file type can't be shown here.
          <button type="button" onClick={download}
            className="rounded-md border border-gray-300 bg-white px-4 py-1.5 font-medium text-gray-700 hover:bg-gray-50">
            Download
          </button>
        </div>
      );
    }

    if (documentPreviewFile && (isImageFile || isTextFile)) {
      return (
        <div ref={scrollContainerRef} onScroll={handleScroll} className="w-full h-full overflow-y-auto">
          {isImageFile ? (
            fileImageUrl && (
              <img src={fileImageUrl} alt={documentPreviewTitle || 'Document'} className="mx-auto max-w-full p-4" onLoad={markReadIfNoScroll} />
            )
          ) : (
            <pre className="whitespace-pre-wrap break-words p-4 text-sm text-gray-800">{fileText ?? ''}</pre>
          )}
        </div>
      );
    }

    if (!documentPreviewUrl && !documentPreviewFile) {
      return (
        <div className="h-full flex items-center justify-center text-gray-500 text-sm">
          No preview available
        </div>
      );
    }

    if (pdfState === false && !documentPreviewUrl) {
      // An in-memory PDF the viewer could not read (there is no link to frame instead).
      return (
        <div className="h-full flex items-center justify-center text-gray-500 text-sm">
          This document could not be displayed.
        </div>
      );
    }

    if (pdfState === false) {
      return (
        <iframe
          title="Document Preview"
          src={documentPreviewUrl}
          className="w-full h-full border-0 block"
          onLoad={(e) => {
            try {
              const win = e.target.contentWindow;
              const doc = win.document;
              const check = () => {
                const el = doc.documentElement;
                const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= 80;
                setIsPreviewScrollCompleted(atBottom);
              };
              check();
              win.addEventListener('scroll', check, { passive: true });
            } catch {
              // Cross-origin content (the Office viewer for old .doc files, or
              // a link fallback) - the page cannot read the frame's scroll
              // position, so the reader confirms manually (see the footer).
              setIsPreviewScrollCompleted(false);
              setIframeReviewUnverifiable(true);
            }
          }}
        />
      );
    }

    return (
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="w-full h-full overflow-y-auto"
      >
        <div className="px-4 pb-4 space-y-4">
          <Document
            file={documentPreviewFile || documentPreviewUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading={
              <div className="flex items-center justify-center h-40">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
              </div>
            }
            error={null}
          >
            {Array.from({ length: numPages }, (_, i) => {
              const isLast = i === numPages - 1;
              return (
                <div key={`page_${i + 1}`} className="mb-4 flex justify-center shadow-sm">
                  <Page
                    pageNumber={i + 1}
                    width={viewerWidth}
                    onRenderSuccess={isLast ? onLastPageRendered : undefined}
                  />
                </div>
              );
            })}
          </Document>
        </div>
      </div>
    );
  };

  // Whether the End User (or other showIAgree role) has actually reached the
  // end of the document content, via the existing PDF viewer's own scroll
  // state (handleScroll / onLastPageRendered) or, for a cross-origin iframe
  // where that's undetectable, the explicit manual confirmation below.
  // Never set to true just because the modal opened or the last page loaded.
  const hasReachedDocumentEnd = isPreviewScrollCompleted;
  const isAlreadyAcknowledged = Number(previewDocumentMeta?.is_acknowledge || 0) === 1;

  return (
    <AnimatePresence>
      {showDocumentPreviewModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-3 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.97 }}
            className={`bg-white rounded-lg w-full shadow-xl overflow-hidden ${
              // The controls step is a short list: a narrower window sized to its content.
              showControlsStep ? 'max-w-3xl max-h-[90vh] flex flex-col' : 'max-w-6xl h-[90vh]'
            }`}
          >
            <div className="shrink-0 flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                {showControlsStep && (
                  <button
                    type="button"
                    onClick={() => setShowControlsStep(false)}
                    className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm text-gray-600 hover:bg-gray-100 shrink-0"
                  >
                    <ArrowLeftIcon className="h-4 w-4" />
                    Back
                  </button>
                )}
                <h3 className="text-sm md:text-base font-semibold text-gray-900 truncate">
                  {showControlsStep ? 'Acknowledge controls' : documentPreviewTitle}
                </h3>
                {showControlsStep && documentPreviewTitle && (
                  <span className="hidden truncate text-sm text-gray-500 sm:inline">· {documentPreviewTitle}</span>
                )}
              </div>
              {!showControlsStep && pendingFiles.length > 1 && (
                <div className="flex items-center gap-2 shrink-0 ml-2">
                  <button
                    type="button"
                    onClick={() => setCurrentFileIndex(Math.max(0, currentFileIndex - 1))}
                    disabled={currentFileIndex === 0}
                    className="px-2 py-1 text-xs text-gray-600 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    ← Prev
                  </button>
                  <span className="text-xs text-gray-500 whitespace-nowrap">
                    {currentFileIndex + 1} / {pendingFiles.length}
                  </span>
                  <button
                    type="button"
                    onClick={() => setCurrentFileIndex(Math.min(pendingFiles.length - 1, currentFileIndex + 1))}
                    disabled={currentFileIndex === pendingFiles.length - 1}
                    className="px-2 py-1 text-xs text-gray-600 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next →
                  </button>
                </div>
              )}
              <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-700 shrink-0 ml-2">
                <XCircleIcon className="w-6 h-6" />
              </button>
            </div>

            <div className={showControlsStep ? 'flex-1 min-h-0 bg-gray-50 overflow-y-auto' : 'h-[calc(90vh-112px)] bg-gray-50 overflow-hidden'}>
              {renderBody()}
            </div>

            <div className="shrink-0 h-14 px-4 border-t border-gray-200 bg-white flex items-center justify-between gap-4">
              {showControlsStep ? (
                <>
                  <span className="text-sm text-gray-600">
                    <span className="font-semibold text-gray-900">{checkedControlIds.length}</span> of{' '}
                    {pendingControlIds.length} selected
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setShowControlsStep(false)}
                      className="px-4 py-1.5 text-sm rounded-md border border-gray-300 bg-white font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      onClick={handleConfirm}
                      disabled={ackLoading || checkedControlIds.length === 0}
                      className="px-4 py-1.5 text-sm rounded-md bg-primary-800 font-medium text-white hover:bg-primary-900 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {ackLoading
                        ? 'Submitting…'
                        : `Acknowledge ${checkedControlIds.length} control${checkedControlIds.length !== 1 ? 's' : ''}`}
                    </button>
                  </div>
                </>
              ) : showApproveReject ? (
                <>
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-gray-500">
                      {pendingFiles.length > 0 && currentFileIndex < pendingFiles.length
                        ? `${pendingFiles[currentFileIndex].control_code || 'Control'} - ${pendingFiles[currentFileIndex].control_name || ''}`
                        : 'Review the document and approve if appropriate'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={onReject}
                      className="px-4 py-1.5 text-sm rounded bg-red-600 text-white hover:bg-red-700"
                    >
                      Reject
                    </button>
                    <button
                      type="button"
                      onClick={onApprove}
                      className="px-4 py-1.5 text-sm rounded bg-green-600 text-white hover:bg-green-700"
                    >
                      Approve
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {showIAgree && !isAlreadyAcknowledged && !hasReachedDocumentEnd && iframeReviewUnverifiable && (
                    <button
                      type="button"
                      onClick={() => setIsPreviewScrollCompleted(true)}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      I have reviewed this document — enable I Agree
                    </button>
                  )}
                  {showIAgree && !isAlreadyAcknowledged && !hasReachedDocumentEnd && !iframeReviewUnverifiable && (
                    <span className="text-xs text-gray-400">
                      Scroll to the end of the document to enable I Agree
                    </span>
                  )}
                  {showIAgree && isAlreadyAcknowledged && (
                    <span className="inline-flex items-center gap-1 text-sm font-medium text-green-600">
                      <CheckCircleIcon className="w-4 h-4" />
                      Acknowledged
                    </span>
                  )}
                  <div className="ml-auto flex items-center gap-2">
                    {showIAgree && (
                      <button
                        type="button"
                        onClick={handleIAgreeClick}
                        disabled={!hasReachedDocumentEnd || isAlreadyAcknowledged}
                        className="px-4 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isAlreadyAcknowledged ? 'Acknowledged' : 'I Agree'}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-4 py-1.5 text-sm rounded border border-gray-300 text-gray-600 hover:bg-gray-50"
                    >
                      Close
                    </button>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default DocumentPreviewModal;
